import React from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { AsyncBoundary, useController, useSuspense } from "@rest-hooks/react";
import {
  UserAdminInfo,
  usersAdminInfoEndpoint,
  usersAdminInfoEndpointPaginated,
} from "../../api/users";
import { InfinitePaginationTable } from "./InfinitePaginationTable";
import DelayedInput from "./DelayedInput";
import { UserActionsMenu } from "./user_actions/UserActionsMenu";

export const UserListPage = () => {
  const [emailFilter, setEmailFilter] = React.useState<string>();
  const { users, nextToken } = useSuspense(usersAdminInfoEndpoint, {
    filter: emailFilter,
  });
  const ctrl = useController();
  const [loading, setLoading] = React.useState(true);

  const columns = [
    { id: "email", label: "Email" },
    { id: "displayName", label: "Name" },
    {
      id: "accountState",
      label: "State",
      render: (user: UserAdminInfo) => (
        <Typography
          sx={{
            color: user.accountState === "CONFIRMED" ? "green" : "red",
          }}
        >
          {user.accountState}
        </Typography>
      ),
    },
    {
      id: "actions",
      label: "Actions",
      render: (user: UserAdminInfo) => (
        <UserActionsMenu user={user} refreshUsers={refreshList} />
      ),
    },
  ];

  const refreshList = () => {
    setLoading(true);
    ctrl.fetch(usersAdminInfoEndpoint, { filter: emailFilter });
  };

  const loadNextPage = () => {
    if (nextToken && nextToken.length > 0) {
      setLoading(true);
      ctrl.fetch(usersAdminInfoEndpointPaginated, {
        nextToken,
        filter: emailFilter,
      });
    }
  };

  const handleFilterChange = React.useCallback(
    (filt: string) => {
      setLoading(true);
      setEmailFilter(filt);
      ctrl.fetch(usersAdminInfoEndpoint, { filter: filt });
    },
    [ctrl, emailFilter]
  );

  React.useEffect(() => {
    setLoading(false);
  }, [ctrl, nextToken, emailFilter]);

  users.sort((a, b) => {
    if (a.email < b.email) {
      return -1;
    } else if (a.email > b.email) {
      return 1;
    }
    return 0;
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box>
        <DelayedInput
          label="Email starts with:"
          variant="outlined"
          value={emailFilter}
          onDelayedChange={(txt) => {
            if (txt !== emailFilter) {
              console.log("email filter", txt);
              handleFilterChange(txt);
            }
          }}
          sx={{
            width: "100%",
          }}
        />
      </Box>
      <InfinitePaginationTable
        columns={columns}
        items={users}
        pageSize={10}
        loading={loading}
        onLoadNext={loadNextPage}
      />
    </Box>
  );
};

export const UserList = () => {
  return (
    <AsyncBoundary>
      <UserListPage />
    </AsyncBoundary>
  );
};
